import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import {
  editSignableDocument,
  getFMMetadatas,
  getSignableDocuments,
  setFMMetadata,
  setFMMetadataLoading,
  setFMMetadataError,
} from './actions'
import { SignableDocument } from 'app/models/document-params'
import {
  LiftSubTradeHoldRequest,
  PlaceSubTradeHoldRequest,
} from 'app/models/hold-params'
import { coalesceThunk } from 'app/state/utils/coalesce-thunk'

export const getSubTradeSignableDocument = (
  subTradeId: string,
  documentType: string,
) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/signable-documents/document-for-signing?id=${subTradeId}&type=${documentType}`,
    )
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('THUNK ERROR', e)
      return {
        success: false,
        payload: e.response?.data?.error ?? 'Unknown error',
      }
    })
}

export const refreshDocument = (subTradeId: string, documentType: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .put(
      `${apiServerUrl}/api/signable-documents/refresh?id=${subTradeId}&type=${documentType}`,
    )
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('THUNK ERROR', e)
      return {
        success: false,
        payload: e.response?.data?.error ?? 'Unknown error',
      }
    })
}

export const getMergedDocument = (subTradeId: string, documentType: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/signable-documents/merged-document?id=${subTradeId}&type=${documentType}`,
    )
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('THUNK ERROR', e)
      return {
        success: false,
        payload: e.response?.data?.error ?? 'Unknown error',
      }
    })
}

export const createSignableDocumentPackets = (
  subTradeIds: string[],
  fmOverrides?: { subTradeId: string; fmPdfBase64: string }[],
) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/signable-documents/packets`, {
      subTradeIds,
      fmOverrides,
    })
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('THUNK ERROR: Create signable document packets failed', e)
      return {
        success: false,
        payload: e.response?.data?.error ?? 'Unknown error',
      }
    })
}

// Open Trade Packet (ADR_LOS-026): per-document payloads for the two zips
// (AA-AL, ADF-TAX) plus the open-trade email model, one master trade.
export const createOpenTradePacket = (subTradeIds: string[]) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/signable-documents/open-trade-packet`, {
      subTradeIds,
    })
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return {
        success: false,
        // 422 marks a validation BLOCK (LOS-6934) rather than a generation
        // failure; the caller must not record a Prep Failed delivery for it.
        status: e.response?.status as number | undefined,
        payload: e.response?.data?.message ?? e.response?.data?.error ?? 'Unknown error',
      }
    })
}

export const signDocument =
  (signnableDocument: SignableDocument, signer: string, metadata: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/signable-documents/sign`, {
        contractId: signnableDocument.contractId,
        contractAdmin: signnableDocument.contractAdmin,
        documentType: signnableDocument.documentType,
        signer,
        metadata,
      })
      .then(document => {
        dispatch(editSignableDocument(document))

        return { success: true, payload: document.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadAllSignableDocuments = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const response = await client.get(
        `${apiServerUrl}/api/signable-documents`,
      )
      const documents = response.data
      dispatch(getSignableDocuments(documents))
    } catch (e) {
      console.log(e)
      dispatch(getSignableDocuments([]))
    }
  },
)

export const getFundingMemoMetadata = (subTradeId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(
      `${apiServerUrl}/api/signable-documents/fm-metadata/one?id=${subTradeId}`,
    )
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('THUNK ERROR', e)
      return {
        success: false,
        payload: e.response?.data?.error ?? 'Unknown error',
      }
    })
}

// Coalesces concurrent refreshes like coalesceThunk, but with two guarantees
// the generic helper cannot provide:
// - a failed refresh PRESERVES the last known metadata — dispatching an empty
//   list on error would flip every FM lock icon in the app to "unlocked";
// - refreshes after a mutation (settle / unsettle / settling AA upload) pass
//   { fresh: true }, which bypasses any request that started before the
//   mutation AND invalidates that request's response, so pre-mutation data
//   can never overwrite post-mutation state.
let lockedFMMetadatasGeneration = 0
let lockedFMMetadatasInFlight: {
  generation: number
  promise: Promise<void>
} | null = null

/**
 * The locked-FM-metadata generation right now. A reader outside this module
 * that fills the store (the Trade detail page's documents read, ADR_LOS-030
 * step 3) captures it before its request and dispatches only if no fresh
 * reload has happened since — the guard loadAllLockedFMMetadatas uses.
 */
export const currentLockedFMMetadatasGeneration = () => lockedFMMetadatasGeneration

/**
 * Starts a fresh generation, as `loadAllLockedFMMetadatas({ fresh: true })`
 * does, for a reader outside this module that is itself the post-write
 * reload (the documents read after a write): a response already in flight
 * from before the write is then dropped.
 */
export const bumpLockedFMMetadatasGeneration = () => ++lockedFMMetadatasGeneration

export const loadAllLockedFMMetadatas =
  (options?: { fresh?: boolean }) => async (dispatch: Dispatch) => {
    if (options?.fresh) {
      lockedFMMetadatasGeneration++
    }
    const generation = lockedFMMetadatasGeneration
    if (
      lockedFMMetadatasInFlight &&
      lockedFMMetadatasInFlight.generation === generation
    ) {
      return lockedFMMetadatasInFlight.promise
    }
    const apiServerUrl = config.backendUrl
    const promise = (async () => {
      try {
        const response = await client.get(
          `${apiServerUrl}/api/signable-documents/fm-metadata`,
        )
        // Drop the response if a fresh refresh superseded this request.
        if (generation === lockedFMMetadatasGeneration) {
          dispatch(getFMMetadatas(response.data))
        }
      } catch (e) {
        console.log(e)
      } finally {
        if (lockedFMMetadatasInFlight?.generation === generation) {
          lockedFMMetadatasInFlight = null
        }
      }
    })()
    lockedFMMetadatasInFlight = { generation, promise }
    return promise
  }

export const toggleFMMetadataLock = (
  subTradeId: string,
  action: 'lock' | 'unlock',
) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/signable-documents/fm-metadata/${action}`, {
      id: subTradeId,
    })
    .then(response => {
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('THUNK ERROR', e)
      return {
        success: false,
        payload: e.response?.data?.error ?? 'Unknown error',
      }
    })
}

// Monotonic per-sub-trade request versions. A response only lands in the
// store if it is still the NEWEST request for that sub-trade — otherwise a
// slow settle-time response could arrive after a later unsettle-time response
// and overwrite fresh lock state with stale data.
const fmMetadataRequestVersion: Record<string, number> = {}

export const loadFMMetadataForSubTrade =
  (subTradeId: string) => async (dispatch: Dispatch) => {
    const version = (fmMetadataRequestVersion[subTradeId] ?? 0) + 1
    fmMetadataRequestVersion[subTradeId] = version
    const isCurrent = () => fmMetadataRequestVersion[subTradeId] === version
    dispatch(setFMMetadataLoading(subTradeId, true))
    try {
      const response = await getFundingMemoMetadata(subTradeId)
      if (!isCurrent()) {
        return
      }
      // Treat a missing payload as a failure too — otherwise the consumer
      // ends up in a "not loading, no data, no error" state that looks like
      // an infinite spinner and re-triggers the dispatch effect on every render.
      if (response.success && response.payload) {
        dispatch(setFMMetadata(subTradeId, response.payload))
      } else {
        dispatch(
          setFMMetadataError(
            subTradeId,
            response.payload ?? 'Failed to load FM metadata',
          ),
        )
      }
    } catch {
      if (isCurrent()) {
        dispatch(setFMMetadataError(subTradeId, 'An unexpected error occurred'))
      }
    }
  }

/**
 * After a hold write the documents are re-read, so the UI shows what the
 * backend minted. `refresh` replaces the tenant-wide reload: the Trade
 * detail page passes its per-trade documents read (ADR_LOS-030 step 3).
 */
type AfterHoldWrite = (dispatch: Dispatch) => Promise<unknown>
const reloadAllSignableDocuments: AfterHoldWrite = (dispatch) =>
  loadAllSignableDocuments()(dispatch)

// Places a document lock (hold) on the given sub-trades. The backend mints
// the Hold (lockId/placedByName/system/origin) from the JWT and the request
// context — the frontend never fabricates it, so on success we refetch the
// affected documents from the ledger rather than optimistically constructing
// a Hold locally.
export const placeHoldOnSubTrades =
  (
    request: PlaceSubTradeHoldRequest,
    placedByName: string,
    refresh: AfterHoldWrite = reloadAllSignableDocuments,
  ) =>
  async (dispatch: Dispatch) => {
    void placedByName // backend derives placedByName from the authenticated user
    const apiServerUrl = config.backendUrl
    try {
      const { data: payload } = await client.post(
        `${apiServerUrl}/api/signable-documents/hold`,
        request,
      )
      await refresh(dispatch)
      return { success: true, payload }
    } catch (err: any) {
      await refresh(dispatch)
      return {
        success: false,
        payload:
          err.response?.data?.error || 'Failed to place lock on sub-trade',
      }
    }
  }

// Lifts the document lock (hold) on the given sub-trades.
export const liftHoldOnSubTrades =
  (
    request: LiftSubTradeHoldRequest,
    refresh: AfterHoldWrite = reloadAllSignableDocuments,
  ) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    try {
      const { data: payload } = await client.delete(
        `${apiServerUrl}/api/signable-documents/hold`,
        { data: request },
      )
      await refresh(dispatch)
      return { success: true, payload }
    } catch (err: any) {
      // Holds are applied per sub-trade server-side and can partially succeed
      // before the backend throws. Resync from the ledger so the UI reflects
      // what actually changed before surfacing the error.
      await refresh(dispatch)
      return {
        success: false,
        payload:
          err.response?.data?.error || 'Failed to lift lock on sub-trade',
      }
    }
  }
